// Omnibar history — the omnibar's shell-style command recall, pure. Pressing
// ArrowUp on the omnibar walks BACKWARD through previously submitted queries
// (most-recent first); ArrowDown walks forward and past the newest returns to
// the live input. Modelled on readline / browser-address-bar / shell history so
// power users arrive with the muscle memory, at zero learning cost for everyone
// else (the key does nothing surprising until you have a history to recall).
//
// This module is that recall as pure types + total folds; the shell
// (AppShellHost) holds an `OmnibarHistory` in React state, calls `record` when a
// query is submitted, and calls `recallOlder` / `recallNewer` from the omnibar
// key handler, placing the returned `value` into the input.
//
// Discipline (dev-ts): pure — no I/O, no Date/random, no id minting; named union
// on a `type` discriminant for the recall result; folds total, `readonly`
// throughout, SAME reference on a no-op. Session-only for MVP: never persisted,
// never event-sourced — thrown away on reload by design. (Lifecycle shortcut,
// stated so it stays visible: cross-session persistence — via the app event log
// or a local store — is a later, additive step; the ring shape below is already
// serialisable, so persistence costs a load/save at the shell edge, no model
// change. See .claude/analysis/overnight-2026-08-19-findings.md.)

// The recall depth. 100 entries is the shell convention (bash HISTSIZE default)
// and comfortably covers a working session without unbounded growth.
export const OMNIBAR_HISTORY_CAP = 100;

// The recorded queries plus a cursor into them.
// - `entries` holds submitted queries, most-recent LAST, deduped MRU-style
//   (re-submitting an old query moves it to the newest slot), capped at
//   OMNIBAR_HISTORY_CAP (oldest dropped first).
// - `cursor === null` — NOT recalling: the live input is whatever the user
//   typed, and history is dormant.
// - `cursor === i` — recalling `entries[i]`: ArrowUp lowers `i` (older),
//   ArrowDown raises it (newer); raising past the newest returns to live.
export type OmnibarHistory = {
    readonly entries: readonly string[];
    readonly cursor: number | null;
};

export const emptyOmnibarHistory: OmnibarHistory = {
    entries: [],
    cursor: null,
};

// The outcome of a recall step — a named sum, never a nullable string (so the
// shell branches on intent, not on a sentinel).
// - `recalled` — place `value` in the input and keep this history (cursor moved).
//   `value` is `""` when the walk returned to the live input (past the newest).
// - `unchanged` — no history, or already at an edge: the shell does nothing and
//   should let the key fall through to its other meaning (listbox navigation).
export type OmnibarRecall =
    | {
          readonly type: "recalled";
          readonly history: OmnibarHistory;
          readonly value: string;
      }
    | { readonly type: "unchanged" };

// Record a submitted query. Trims; ignores empty; removes any existing equal
// entry then appends (MRU dedup — a re-run query jumps to newest, no stale
// duplicates); caps at OMNIBAR_HISTORY_CAP dropping the oldest. Always resets
// the cursor to dormant — a submission ends any in-flight recall.
export const record = (
    history: OmnibarHistory,
    query: string,
): OmnibarHistory => {
    const trimmed = query.trim();
    if (trimmed === "") {
        // Nothing to record, but a submit still ends recall: only rebuild when
        // the cursor was actually engaged (SAME reference on a true no-op).
        return history.cursor === null
            ? history
            : { entries: history.entries, cursor: null };
    }
    const withoutDup = history.entries.filter((e) => e !== trimmed);
    const appended = [...withoutDup, trimmed];
    const capped =
        appended.length > OMNIBAR_HISTORY_CAP
            ? appended.slice(appended.length - OMNIBAR_HISTORY_CAP)
            : appended;
    return { entries: capped, cursor: null };
};

// ArrowUp — walk one step older. From dormant, start at the newest entry; from a
// live cursor, step toward index 0; at the oldest (index 0), stay put and report
// `unchanged` (no wrap — the shell convention). Empty history → `unchanged`.
export const recallOlder = (history: OmnibarHistory): OmnibarRecall => {
    const last = history.entries.length - 1;
    if (last < 0) return { type: "unchanged" };
    const next = history.cursor === null ? last : history.cursor - 1;
    if (next < 0) return { type: "unchanged" };
    return {
        type: "recalled",
        history: { entries: history.entries, cursor: next },
        value: history.entries[next] ?? "",
    };
};

// ArrowDown — walk one step newer. Dormant → `unchanged` (already live). From a
// cursor, step toward the newest; stepping past the newest returns to the live
// input: cursor dormant again, value `""` (the shell clears the field).
export const recallNewer = (history: OmnibarHistory): OmnibarRecall => {
    if (history.cursor === null) return { type: "unchanged" };
    const last = history.entries.length - 1;
    if (history.cursor >= last) {
        return {
            type: "recalled",
            history: { entries: history.entries, cursor: null },
            value: "",
        };
    }
    const next = history.cursor + 1;
    return {
        type: "recalled",
        history: { entries: history.entries, cursor: next },
        value: history.entries[next] ?? "",
    };
};

// The user edited the input — drop the recall cursor so the typed text is live
// and the next ArrowUp starts a fresh walk from the newest. SAME reference when
// already dormant.
export const resetCursor = (history: OmnibarHistory): OmnibarHistory =>
    history.cursor === null
        ? history
        : { entries: history.entries, cursor: null };
